/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useContext } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';

import {
	AUTOMATION_DISCOVERY_PAGE_PUBLIC_LINK,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import {
	Attribution,
	ErrorDisplay,
	withTransparentErrorBoundary,
} from '@confluence/error-boundary';
import { keys } from '@confluence/storage-manager';
import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';

import type {
	PagePublicLinkAutomationDiscoveryDialogCheckSpaceAdminQuery as PagePublicLinkAutomationDiscoveryDialogCheckSpaceAdminQuery$data,
	PagePublicLinkAutomationDiscoveryDialogCheckSpaceAdminQueryVariables as PagePublicLinkAutomationDiscoveryDialogCheckSpaceAdminQuery$variables,
} from './__types__/PagePublicLinkAutomationDiscoveryDialogCheckSpaceAdminQuery';
import { BaseAutomationDiscoveryDialog } from './BaseAutomationDiscoveryDialog';
import { EventSourceName } from './utils/analytics';

const i18n = defineMessages({
	publicLinkTurnedOnHeader: {
		id: 'automation-discovery.page-public-link.public-link-turned-on.header',
		defaultMessage: 'Your public link was turned on and copied',
		description:
			'A header telling the user the public link was turned on and copied to their clipboard',
	},
	publicLinkTurnedOffHeader: {
		id: 'automation-discovery.page-public-link.public-link-turned-off.header',
		defaultMessage: 'Your public link was turned off',
		description: 'A header telling the user the public link was turned of',
	},
	ctaText: {
		id: 'automation-discovery.page-public-link.cta.text',
		defaultMessage: 'Auto-turn off public links after X days?',
		description:
			'A header asking the user if they want to auto-turn off public links after a certain number of days have passed',
	},
});

type PagePublicLinkAutomationDiscoveryDialogProps = {
	onClose: () => void;
	isPublicLinksToggleOn?: boolean;
	spaceKey: string | null;
};

const PagePublicLinkAutomationDiscoveryDialogComponent = ({
	onClose,
	isPublicLinksToggleOn,
	spaceKey,
}: PagePublicLinkAutomationDiscoveryDialogProps) => {
	const { edition } = useSessionData();
	const isPremiumEdition = edition === ConfluenceEdition.PREMIUM;
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { data: spaceAdminData, error: spaceAdminError } = useQuery<
		PagePublicLinkAutomationDiscoveryDialogCheckSpaceAdminQuery$data,
		PagePublicLinkAutomationDiscoveryDialogCheckSpaceAdminQuery$variables
	>(
		gql`
			query PagePublicLinkAutomationDiscoveryDialogCheckSpaceAdminQuery($spaceKey: String) {
				space(key: $spaceKey) {
					id
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			variables: {
				spaceKey,
			},
			skip: !spaceKey || !isPremiumEdition,
		},
	);

	const isSpaceAdmin = Boolean(spaceAdminData?.space?.currentUser.isAdmin);

	if (spaceAdminError) {
		experienceTracker.fail({
			name: AUTOMATION_DISCOVERY_PAGE_PUBLIC_LINK,
			error: spaceAdminError,
		});

		return spaceAdminError && <ErrorDisplay error={spaceAdminError} />;
	}

	const header = isPublicLinksToggleOn ? (
		<FormattedMessage {...i18n.publicLinkTurnedOnHeader} />
	) : (
		<FormattedMessage {...i18n.publicLinkTurnedOffHeader} />
	);

	return isPremiumEdition && isSpaceAdmin ? (
		<BaseAutomationDiscoveryDialog
			eventSourceName={EventSourceName.PAGE_PUBLIC_LINKS}
			discoveryExperience="automationDiscoveryPagePublicLink"
			localStorageKey={keys.AUTOMATION_DISCOVER_PAGE_PUBLIC_LINK}
			onClose={onClose}
			spaceKey={spaceKey}
			templateUrl={`/wiki/spaces/${spaceKey}/settings/automation#/rule/template?templateId=confluence_collaborate_template_12`}
			templateName="scheduledInactivePagesDisablePublicLink"
			cta={<FormattedMessage {...i18n.ctaText} />}
			header={header}
			testId="public-link-success-dialog"
		/>
	) : null;
};

export const PagePublicLinkAutomationDiscoveryDialog = withTransparentErrorBoundary({
	attribution: Attribution.AUTOMATION_FOR_CONFLUENCE,
})(PagePublicLinkAutomationDiscoveryDialogComponent);
